import { FormInputElement } from '@typedream/core';
import { setNodes, TNodeEntry } from '@udecode/plate-core';
import { DreamEditor } from '../../../types';
import { FormEditor } from '../types';

export const normalizeFormInputName = (
  editor: DreamEditor,
  entry: TNodeEntry<FormInputElement>,
) => {
  const e = editor as FormEditor;

  // isNodeDuplicated checks if the given form input is a duplicate
  // of existing form input
  const isNodeDuplicated = (node: FormInputElement): boolean => {
    const { id, name, label } = node;
    return e.currentInputNameToLabelMapping[name] === label
      && e.currentInputNameToBlockIDMapping[name] !== id;
  };

  // isNodePasted checks if the given node is pasted from another source
  // (e.g. another form)
  const isNodePasted = (node: FormInputElement): boolean => {
    const { id, name, label } = node;
    return e.currentInputNameToLabelMapping[name] !== label
    && e.currentInputNameToBlockIDMapping[name] !== id;
  };

  // requestNewFormSchemaID creates new form input schema id
  const requestNewFormSchemaID = (ed: FormEditor): string => {
    const fData = ed.currentInputNameToLabelMapping;

    if (fData) {
      let maxV = -1;
      Object.keys(fData).forEach((v) => {
        const vNum = parseInt(v, 10);
        if (vNum > maxV) maxV = vNum;
      });
      return (maxV + 1).toString();
    }

    return '0';
  };

  const [node, path] = entry;
  if (node.type === 'form_input' && !node.name) {
    const { label } = node;

    let nextId: string;
    // if label existed before.
    if (e.initialLabelToNameMapping[label as string]) {
      nextId = e.initialLabelToNameMapping[label as string];
    } else {
    // request new id
      nextId = requestNewFormSchemaID(e);
    }

    setNodes(e, {
      name: nextId,
    }, {
      at: path,
    });
    // eslint-disable-next-line no-param-reassign
    e.currentInputNameToLabelMapping[nextId] = node.label as string;
    e.currentInputNameToBlockIDMapping[nextId] = node.id;
    return true;
  }
  if (node.type === 'form_input' && !!node.name) {
    // handles first time normalization, e.currentInputNameToLabelMapping is empty
    if (!e.currentInputNameToBlockIDMapping[node.name as string]) {
      e.currentInputNameToBlockIDMapping[node.name as string] = node.id as string;
    }

    // handles first time normalization, e.currentInputNameToLabelMapping is empty
    if (!e.currentInputNameToLabelMapping[node.name as string]) {
      e.currentInputNameToLabelMapping[node.name as string] = node.label as string;
    }

    if (isNodePasted(node)) {
      const { label } = node;

      let nextId: string;
      // if label existed before.
      if (e.initialLabelToNameMapping[label as string]) {
        nextId = e.initialLabelToNameMapping[label as string];
      } else {
        // request new id
        nextId = requestNewFormSchemaID(e);
      }

      setNodes(e, {
        name: nextId,
      }, {
        at: path,
      });

      e.currentInputNameToLabelMapping[nextId] = node.label as string;
      e.currentInputNameToBlockIDMapping[nextId] = node.id;
      return true;
    }

    if (isNodeDuplicated(node)) {
      // request new id
      const nextId = requestNewFormSchemaID(e);
      const nodeLabel = `${node.label} Duplicate #${nextId}`;
      setNodes(e, {
        name: nextId,
        label: nodeLabel,
      }, {
        at: path,
      });

      e.currentInputNameToLabelMapping[nextId] = nodeLabel;
      e.currentInputNameToBlockIDMapping[nextId] = node.id;
      return true;
    }
  }
  return false;
};
